import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from '../../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/ui/Button';

const ResourceEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(id ? true : false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'General',
    excerpt: '',
    content: '',
    published: false
  });

  useEffect(() => {
    if (id) {
      const fetchResource = async () => {
        const { data, error } = await supabase
          .from('resources_1779569461628')
          .select('*')
          .eq('id', id)
          .single();

        if (data) {
          setFormData(data);
        }
        setFetching(false);
      };
      fetchResource();
    }
  }, [id]);

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');
    setFormData({ ...formData, slug });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...formData,
      // slug must be unique, so we ensure it's not empty
      slug: formData.slug || formData.title.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')
    };

    let error;
    if (id) {
      const { error: updateError } = await supabase
        .from('resources_1779569461628')
        .update(payload)
        .eq('id', id);
      error = updateError;
    } else {
      const { error: insertError } = await supabase
        .from('resources_1779569461628')
        .insert([payload]);
      error = insertError;
    }

    if (error) {
      alert('Error saving resource: ' + error.message);
      setLoading(false);
    } else {
      navigate('/admin/resources');
    }
  };

  if (fetching) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-teal w-10 h-10" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => navigate('/admin/resources')} 
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Go Back"
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="w-6 h-6 text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-brand-navy leading-tight">
            {id ? 'Edit Resource' : 'Create New Resource'}
          </h1>
          <p className="text-sm text-gray-500">Draft or publish articles for your audience.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2 md:col-span-1">
              <label className="block text-sm font-bold text-brand-navy mb-1.5">Title</label>
              <input 
                type="text" 
                name="title"
                required 
                value={formData.title}
                onChange={handleInputChange}
                onBlur={() => !formData.slug && generateSlug()}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal outline-none transition-all"
                placeholder="e.g., Why Your Nonprofit Needs an AI Policy"
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="block text-sm font-bold text-brand-navy mb-1.5">URL Slug</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  name="slug"
                  required 
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal outline-none transition-all font-mono text-sm"
                  placeholder="why-nonprofit-needs-ai-policy"
                />
                <button 
                  type="button" 
                  onClick={generateSlug} 
                  className="p-3 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" 
                  title="Generate from title"
                >
                  <SafeIcon icon={FiIcons.FiRefreshCw} />
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-brand-navy mb-1.5">Category</label>
              <select 
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal outline-none transition-all"
              >
                <option value="General">General</option>
                <option value="AI Governance">AI Governance</option>
                <option value="Responsible AI">Responsible AI</option>
                <option value="Case Study">Case Study</option>
                <option value="Tutorial">Tutorial</option>
              </select>
            </div>
            <div className="flex items-end pb-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative inline-flex items-center">
                  <input 
                    type="checkbox" 
                    name="published"
                    className="sr-only peer"
                    checked={formData.published}
                    onChange={handleInputChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-teal/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-teal"></div>
                </div>
                <span className="text-sm font-bold text-brand-navy group-hover:text-brand-teal transition-colors">Published (Live on site)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-navy mb-1.5">Excerpt</label>
            <textarea 
              name="excerpt"
              rows="3"
              value={formData.excerpt}
              onChange={handleInputChange}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal outline-none transition-all"
              placeholder="A brief summary for the resources list page..."
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-navy mb-1.5">Content (Markdown)</label>
            <textarea 
              name="content"
              rows="15"
              required
              value={formData.content}
              onChange={handleInputChange}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal outline-none transition-all font-mono text-sm leading-relaxed"
              placeholder="# Use Markdown for formatting&#10;&#10;Explain your insights here..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button onClick={() => navigate('/admin/resources')} variant="outline" className="bg-white">
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading} className="min-w-[160px]">
            {loading ? (
              <>
                <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (id ? 'Update Resource' : 'Create Resource')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ResourceEditor;